/*global defaultSearchParameters */
import Parameters from '../../../Templates/Core/JS/Parameters.js';
import { FetchQueue } from '../../../Templates/Core/JS/FetchQueue.js';
import Configuration from '../../../Templates/Core/JS/Configuration.js';
import { Suggestion } from './Suggestion.js';

/**
 * The AutoComplete class encapsulating the input field.
 */

export class AutoComplete {
	// eslint-disable-line no-unused-vars

	/**
	 * Initialise the AutoComplete
	 *
	 * @return {Boolean}
	 */

	constructor(inputField, index, searchRequest = null) {
		this.inputValues = {
			id: null,
			name: null,
			keyword: ''
		};

		this._index = index;
		this._inputField = inputField;
		this.originalValue = '';
		this.interaction = '';

		this._focusableElements = [];
		this._focusPointer = -1;
		this._suggestions = [];
		this._maxResults = 4;

		// Add the containing element

		this._container = document.createElement('div');
		this._container.className = 'AutoCompleteContainer';
		this._container.innerHTML = '<ul class="AutoComplete"></ul>';

		this._inputField.parentNode.appendChild(this._container);

		const form = this._inputField.closest('form');
		this._formID = form.id;
		this._button = form.querySelector('button[type=submit]');

		// Suggest based on the active input's type

		this.suggestType = inputField.dataset.type;
		if (!this.suggestType) {
			this.suggestType = 'all';
		}

		// Retrieve the default parameters from the tp4 file.

		this.searchParameters = defaultSearchParameters;

		if (searchRequest !== null) {
			this.searchParameters = searchRequest.getQParameters();
		}

		document.addEventListener('url_change', (data) => {
			this.searchParameters = data.detail.searchRequest.getQParameters();
		});

		document.addEventListener('search_complete', () => {
			this._inputField.value = '';
		});

		window.EventAggregationService.subscribeTo('SearchPageClearFiltersEvent', {
			notify: (event) => {
				// Clear the selected value if selected filter contains discipline or keyword what
				if(event.filters.includes('di') || event.filters.includes('kw-what')) {
					this.selectedValue = null;
				}
				// Clear the selected value if deleted filter is country, organisation, region or keyword where
				if(event.filters.includes('ci') || event.filters.includes('oi') || event.filters.includes('rg') || event.filters.includes('kw-where')) {
					this.selectedValue = null;
				}
			}
		})
	}

	/**
	 * Returns the index of the autoComplete in the AutoComplete it belongs to.
	 *
	 * @return {number}
	 */

	get index() {
		return this._index;
	}

	/**
	 * Get the submit button attached to this autocomplete field.
	 *
	 * @return {Element}
	 */

	get submitButton() {
		return this._button;
	}

	/**
	 * Get the ID of the containing form of to this autocomplete field.
	 *
	 * @return {String}
	 */

	get formID() {
		return this._formID;
	}

	/**
	 * Gets the value of the AutoComplete's input field.
	 *
	 * @return {string}
	 */

	get inputValue() {
		return this._inputField.value;
	}

	/**
	 * Sets the AutoComplete's input current value to the one specified.
	 *
	 * Remembers the cursor position to avoid weird jumping behaviour
	 *
	 * @param {string} value
	 */

	set inputValue(value) {
		const { selectionStart, selectionEnd } = this._inputField;
		this._inputField.value = value;
		this._inputField.selectionStart = selectionStart;
		this._inputField.selectionEnd = selectionEnd;
	}

	/**
	 * Trigger a suggestion.
	 *
	 * @param {function} callback
	 * @return void
	 */

	suggest(callback = () => {}) {
		this.moveSelectedTo(-1);

		// Check to see if we need to make a new request or not

		const value = this._inputField.value;
		if (value === '') {
			this.hide();
			callback([]);
		} else {
			this._suggestions = [];

			if (value.length > 0) {
				// eslint-disable-next-line no-console
				console.info('AutoComplete:_Suggest', value);

				// The degree property cannot be used as part of the context

				if (this.searchParameters.degrees) {
					delete this.searchParameters.degrees;
				}

				const data = {
					type: this.suggestType,
					keyword: encodeURIComponent(value),
					context: Parameters.alias(this.searchParameters)
				};

				this._doRequest(data, callback);
			}
		}
	}

	/**
	 * Shows the autocomplete container and resets the selection index
	 */

	show() {
		if (this.inputValue.length === 0) {
			return;
		}

		this.moveSelectedTo(-1);
		this._container.classList.remove('Hidden');
	}

	/**
	 * Hides the autocomplete container and resets the selection index
	 */

	hide() {
		this.moveSelectedTo(-1);
		this._container.classList.add('Hidden');
	}

	/**
	 * Focuses the AutoComplete's input
	 *
	 * Provides the extra benefit of remembering a users cursor position
	 */

	focus() {
		const { selectionStart, selectionEnd } = this._inputField;
		this._inputField.focus();
		this._inputField.selectionStart = selectionStart;
		this._inputField.selectionEnd = selectionEnd;
	}

	/**
	 * Blur the input field and reset the cursor position to the end
	 */

	blur() {
		this._inputField.blur();
		this._inputField.selectionStart = this._inputField.value.length;
		this._inputField.selectionEnd = this._inputField.value.length;
	}

	/**
	 * Copy the selected option's text content to the input field
	 */

	copySelectionText() {
		this.originalValue = this._inputField.value;
		const selected = this._container.getElementsByClassName('Selected');

		if (selected.length > 0) {
			this.inputValue = selected[0].innerText;
		}
	}

	/**
	 * @return {void}
	 */
	setSelectedValue() {
		const selected = this._container.getElementsByClassName('Selected');
		if (selected.length > 0) {
			const selectedElement = selected[0];

			this.selectedValue = {
				id: parseInt(selectedElement.dataset.paramValue, 10),
				name: selectedElement.dataset.paramName,
				position: parseInt(selectedElement.dataset.position, 10) + 1,
				title: selectedElement.innerText
			};
			return;
		}

		this.selectedValue = null;
	}

	/**
	 * Get the position of the currently selected item
	 * @return {string}
	 */
	get selectedPosition() {
		return this.selectedValue ? this.selectedValue.position.toString() : '';
	}

	/**
	 * Get a hyphenated name-value representation of the selected item
	 * @return {string}
	 */
	get matchedValue() {
		return this.selectedValue ? `${this.selectedValue.name}-${this.selectedValue.id}` : '';
	}

	/**
	 * Move focus to the element that corresponds with the given pointer.
	 *
	 * -1 results in the keyword input, > 0 is one of the suggestions
	 *
	 * @param {Number} pointerNumber
	 * @return void
	 */

	moveSelectedTo(pointerNumber) {
		if (typeof pointerNumber !== 'number') {
			console.warn('AutoComplete.moveSelectedTo: Invalid pointerNumber provided, not of right type', pointerNumber);
			return;
		}

		if (pointerNumber === -1) {
			// Move selected class to search input

			Array.from(this._container.getElementsByClassName('Selected')).forEach((selected) => {
				selected.classList.remove('Selected');
			});
		} else {
			// Check if there are any elements that can be focused

			if (this._focusableElements.length > 0) {
				if (this._focusableElements[pointerNumber]) {
					Array.from(this._container.getElementsByClassName('Selected')).forEach((selected) => {
						selected.classList.remove('Selected');
					});

					this._focusableElements[pointerNumber].classList.add('Selected');
				} else {
					// eslint-disable-next-line no-console
					console.info('AutoComplete.moveSelectedTo: Trying to ' + 'focus an element that does not exist');
					return;
				}
			} else {
				// eslint-disable-next-line no-console
				console.info(
					'AutoComplete.moveSelectedTo:\
					Tried to move focus, but no focusable Elements have been set,\
					moving focus to input'
				);
				this.moveSelectedTo(-1);
				return;
			}
		}

		this._focusPointer = pointerNumber;
	}

	/**
	 * Move focus to the previous suggestion (the one above the current)
	 *
	 * @return void
	 */

	moveSelectedUp() {
		this.moveSelectedTo(this._focusPointer - 1);
	}

	/**
	 * Move focus to the next suggestion (the one below the current)
	 *
	 * @return void
	 */

	moveSelectedDown() {
		this.moveSelectedTo(this._focusPointer + 1);
	}

	/**
	 * Attaches the specified callback to the on selection changed event
	 *
	 * @param {string} event
	 * @param callback
	 */

	addEventListener(event, callback) {
		this._inputField.addEventListener(event, callback);
	}

	/**
	 * Attaches the specified callback to the on selection changed event
	 *
	 * @param callback
	 */

	onSelectionChange(callback) {
		this._inputField.addEventListener('selectionChange', callback);
	}

	/**
	 *
	 * Retrieve a list of suggestions based on the current input value
	 *
	 * @return void
	 */

	updateSelectedFilter(callback) {
		if (this.selectedValue) {
			this.setFilterValuesFromCurrentSelection();
			callback();
			return;
		}

		this.suggest((suggestions) => {
			let exactMatch = null;
			suggestions.some((suggestion) => {
				if (suggestion.isExactMatch) {
					exactMatch = suggestion;
					return true;
				}
			});

			if (exactMatch !== null) {
				this.setFilterValues(exactMatch);
				callback();
				return;
			}

			this.setKeywordValues();
			this.hide();
			callback();
		});
	}

	/**
	 * Set the input values for this autocomplete as a keyword
	 */

	setKeywordValues() {
		this.inputValues.id = null;
		this.inputValues.name = null;
		this.inputValues.keyword = this._inputField.value;
		this.inputValues.title = this.inputValues.keyword;
	}

	/**
	 * Set the input values for this autocomplete as a filter
	 *
	 * @param {object} suggestion
	 */

	setFilterValues(suggestion) {
		this.inputValues.id = suggestion.id;
		this.inputValues.name = suggestion.paramName;
		this.inputValues.title = suggestion.title;
		this.inputValues.keyword = '';
	}

	/**
	 * Set the input values based on the selected autocomplete item
	 * @return {void}
	 */

	setFilterValuesFromCurrentSelection() {
		this.inputValues.id = this.selectedValue.id;
		this.inputValues.name = this.selectedValue.name;
		this.inputValues.title = this.selectedValue.title;
		this.inputValues.keyword = '';
	}

	/**
	 * Render the full list of suggestions.
	 *
	 * This function replaces an old entry if there are elements already present
	 * in the dropdown. Otherwise constructs one from scratch.
	 *
	 * @returns void
	 *
	 * @private
	 */

	_render() {
		if (this._suggestions.length === 0) {
			return;
		}

		let list = this._container.getElementsByTagName('ul')[0];
		const oldSuggestions = Array.from(list.getElementsByClassName('Suggestion'));

		Array.from(list.getElementsByClassName('AutoCompleteHeading')).forEach((heading) => {
			heading.remove();
		});

		this._suggestions.splice(this._maxResults);
		this._sortSuggestionsByType();

		// Iterate over the suggestions

		this._suggestions.forEach((suggestion, index) => {
			const elementSuggestion = suggestion.render();

			elementSuggestion.addEventListener('mousedown', () => {
				this.interaction = 'click';
				this.moveSelectedTo(index);
				this._inputField.dispatchEvent(new Event('selectionChange'));
			});

			elementSuggestion.addEventListener('mouseover', () => {
				this.moveSelectedTo(index);
			});

			// Add Headings for all the fields

			const collection = Array.from(list.getElementsByClassName('Suggestion'));

			list = AutoComplete._addHeadings(list, suggestion, index, collection);

			// Replace the suggestion rather than injecting anew
			// if there is an element that can be replaced

			if (index < oldSuggestions.length) {
				oldSuggestions[index].parentNode.replaceChild(elementSuggestion, oldSuggestions[index]);
				return;
			}

			list.appendChild(elementSuggestion);
		});

		// Final check to see if length of newly generated suggestions is smaller
		// than the old. If so, then destroy the excess suggestions.

		if (oldSuggestions.length !== 0 && oldSuggestions.length > this._suggestions.length) {
			const elementSuggestions = Array.from(list.getElementsByClassName('Suggestion'));

			for (let i = oldSuggestions.length - 1; i >= this._suggestions.length; i--) {
				elementSuggestions[i].remove();
			}
		}

		this._focusableElements = Array.from(this._container.getElementsByClassName('Suggestion'));

		if (document.activeElement === this._inputField) {
			this.show();
		}
	}

	/**
	 * Group suggestions by the type of the first suggestion.
	 *
	 * @private
	 */

	_sortSuggestionsByType() {
		const distinctTypes = [...new Set(this._suggestions.map((x) => x['type']))];
		const sortedSuggestions = [];

		distinctTypes.forEach((type) => {
			const suggestionsOfType = this._suggestions.filter((suggestion) => suggestion['type'] === type);
			sortedSuggestions.push(...suggestionsOfType);
		});

		this._suggestions = sortedSuggestions;
	}

	/**
	 * Add the headings to the AutoComplete
	 *
	 * Provides the logic to add, remove and move headings around the auto complete
	 * to correspond with the start of the first specialisation and first
	 * discipline.
	 *
	 * @param list 			- the rendered list of suggestions (to include headings)
	 * @param suggestion		- instance of class Suggestion that is being rendered
	 * @param index			- the index of the current item being inserted
	 * @param collection		- the collection of suggestion items only, in the list
	 * @returns {HTMLElement}
	 *
	 * @private
	 */

	static _addHeadings(list, suggestion, index, collection) {
		const headerType = suggestion.type;
		if (!list.querySelector(`.AutoCompleteHeading.${headerType}`)) {
			const heading = document.createElement('li');
			heading.innerText = headerType;
			heading.className = `AutoCompleteHeading ${headerType}`;

			if (index === 0) {
				list.insertBefore(heading, collection[0]);
			} else {
				list.insertBefore(heading, collection[index]);
			}
		}

		return list;
	}

	/**
	 * Get the search parameter name based on the given suggestion type
	 *
	 * @param suggestionType
	 * @param numSuggestions
	 * @private
	 * @return string
	 */

	static _getParamName(suggestionType, numSuggestions) {
		let paramName = '';

		switch (suggestionType) {
			case 'specialisation':
			case 'discipline':
				paramName = 'discipline_ids';
				break;

			case 'country':
				paramName = 'country_ids';
				break;

			case 'organisation':
				// check whether the count value is greater than 0

				if (numSuggestions <= 0) {
					// bail out when there are no studies
					break;
				}

				paramName = 'organisation_ids';
				break;

			case 'continent':
				paramName = 'region_ids';
				break;

			default:
				console.error('AutoComplete: entity does no have a valid type');
				break;
		}

		return paramName;
	}

	/**
	 * The onSuccess function of the request.
	 *
	 * @param entities
	 * @param callback
	 * @return void
	 *
	 * @private
	 */

	_requestSuccess(entities, callback) {
		// Get the data and build the list of suggestions

		const suggestions = [];

		entities.forEach((value, index) => {
			const paramName = AutoComplete._getParamName(value.type, value.count);

			if (!paramName) {
				// No suggestion should be pushed invalid type is present
				return;
			}

			const suggestion = new Suggestion(value.name, value.type, paramName, value.id, value.is_exact_match, index);
			suggestions.push(suggestion);
		});

		// Push them through to AutoComplete

		if (suggestions.length === 0) {
			callback([]);
			return;
		}

		// Add the suggestions

		this._suggestions = suggestions;

		// Render the results

		this._render();
		this.setAutoCompleteWidth();

		// In case after performing a search the user has already emptied the input field,
		// hide the autocomplete and update the selection once more.

		const inputValue = this.inputValue;
		if (inputValue === '') {
			this.hide();
		}

		callback(suggestions);
	}

	/**
	 * Convenience function for fetching suggestions.
	 *
	 * This function fetches the data from the ServiceLayer and adds it to the
	 * AutoComplete class.
	 *
	 * @param data - the data object to be used for Request.data
	 * @param callback - whether to have an async request or not
	 * @return void
	 * @private
	 */

	_doRequest(data, callback) {
		// Param checking

		if (!data || !data.keyword) {
			console.warn('AutoComplete._doRequest: Invalid data object encountered');
			callback();
			return;
		}

		let params = [];
		let url = `${Configuration.autoCompleteURL}?`;

		Object.keys(data).forEach((dataKey) => {
			params.push(`${dataKey}=${data[dataKey]}`);
		});

		url += params.join('&');

		const dataSend = {
			method: 'get',
			url,
			mode: 'cors'
		};

		FetchQueue.addRequest(dataSend.url, dataSend)
			.then((response) => {
				if (response.ok) {
					return response.json();
				}
			})
			.then((jsonData) => {
				this._requestSuccess(jsonData, callback);
			})
			.catch(() => {
				console.error('The autocomplete microservice did not respond with a 200 OK.');
				/*
				 * Return an empty list of suggestions to prevent this error
				 * from breaking the entire search functionality.
				 */
				this._requestSuccess([], callback);
			});
	}

	/**
	 * Set output max-width same as searchInput width.
	 *
	 * @return void
	 */

	setAutoCompleteWidth() {
		this._container.style.width = this._inputField.getBoundingClientRect().width + 'px';
	}
}
